/**
 * Renders tenant HTML on the preview origin.
 *
 * The dashboard never puts a template's markup in its own document. It frames a
 * static shell served from OC_PREVIEW_URL — a different origin — and posts the
 * HTML across. The shell puts it in a nested `sandbox=""` frame, so the markup
 * ends up two hops from this session: a different origin, then an opaque one.
 *
 * That is the whole reason for the separate origin, and why the server refuses
 * to start when it matches OC_PUBLIC_URL. Without it, a template written by any
 * member of an organization is stored XSS against every other member who opens
 * it — the iframe sandbox is a second layer, not a substitute for an origin.
 *
 * The dashboard only ever sends. It reads nothing back out of the frame, so
 * there is no path from rendered markup into this document at all.
 */

import { useEffect, useRef, useState } from "react";

import { useSession } from "../session";

interface TemplatePreviewProps {
  html: string;
  /** Height of the viewport. The frame scrolls; it does not report its size. */
  height?: number | string;
  /** Narrows the frame to check a mobile layout. */
  width?: number | null;
  title?: string;
}

/** Matches the contract in internal/preview. */
const MESSAGE_RENDER = "oc-preview";

export function TemplatePreview({
  html,
  height = 420,
  width = null,
  title = "Template preview",
}: TemplatePreviewProps) {
  const { session } = useSession();
  const origin = session?.preview_url ?? "";

  const frame = useRef<HTMLIFrameElement>(null);

  // A message posted before the shell's listener exists is simply lost, so the
  // first render has to wait for the frame to load. The load event is the right
  // signal and the only one available: the shell cannot announce itself, since
  // a sandboxed frame gets no referrer and so cannot tell which of its allowed
  // origins is actually embedding it.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ready || !origin) return;

    // Addressed to the exact origin rather than "*". With a wildcard the
    // message goes wherever the frame happens to have navigated, which is the
    // one thing this design is trying to prevent.
    frame.current?.contentWindow?.postMessage({ type: MESSAGE_RENDER, html }, origin);
  }, [ready, origin, html]);

  // A deployment that somehow has no preview origin gets an honest empty state
  // rather than a blank rectangle. Rendering the markup here instead would be
  // the exact vulnerability the origin exists to close.
  if (!origin) {
    return (
      <div className="preview-frame preview-frame--empty" style={{ height }}>
        <div className="oc-empty">
          <div className="oc-empty__title">No preview origin configured</div>
          <div className="oc-empty__hint">
            Previews render on their own origin so a template cannot reach this session. Set{" "}
            <span className="oc-mono-sm">OC_PREVIEW_URL</span> to enable them.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preview-frame" style={{ padding: 0, height }}>
      <iframe
        ref={frame}
        title={title}
        src={origin}
        referrerPolicy="no-referrer"
        // Fires after the shell's script has run, so its listener is installed
        // by the time anything is posted.
        onLoad={() => setReady(true)}
        style={{
          display: "block",
          width: width ? `${width}px` : "100%",
          height: "100%",
          border: "none",
          background: "#fff",
          // Centred when narrowed, so the mobile width reads as a device rather
          // than a broken layout.
          margin: width ? "0 auto" : undefined,
        }}
      />
    </div>
  );
}
